import { ChangeEvent, useCallback, useEffect, useState } from "react";

interface Price {
  min: number;
  max: number;
}
interface Props {
  handleRange: (values: number[]) => void;
  priceFilter: Price;
  minPrice?: number;
  maxPrice?: number;
}

interface RangePayload {
  onSliderChange: (values: number[]) => void;
  onInputChange: (index: number) => (e: ChangeEvent<HTMLInputElement>) => void;
  onBlurHandler: (index: number) => () => void;
  initialMinPrice: number;
  initialMaxPrice: number;
  range: string[];
}

export default function useRange({
  priceFilter,
  minPrice,
  maxPrice,
  handleRange,
}: Props): RangePayload {
  const initialMinPrice = priceFilter?.min;
  const initialMaxPrice = priceFilter?.max;
  const minPriceValue = minPrice || initialMinPrice;
  const maxPriceValue = maxPrice || initialMaxPrice;

  const [range, setRange] = useState<string[]>([
    minPriceValue.toString(),
    maxPriceValue.toString(),
  ]);

  const [inputActing, setInputActing] = useState(false);

  const onMinChange = (val: string) => {
    const value = Math.max(
      initialMinPrice,
      Math.min(Number(val), initialMaxPrice)
    );
    const newMaxValue = Math.max(Number(range[1]), value);
    setRange([value.toString(), newMaxValue.toString()]);
    handleRange([value, newMaxValue]);
  };

  const onMaxChange = (val: string) => {
    const value = Math.min(
      Math.max(Number(val), initialMinPrice),
      initialMaxPrice
    );
    const newMinValue = Math.min(
      range[0] === null ? minPriceValue : Number(range[0]),
      value
    );
    setRange([newMinValue.toString(), value.toString()]);
    handleRange([newMinValue, value]);
  };

  const onInputChange =
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      setInputActing(true);
      const value = e.target.value;
      const newInputValue = range.slice();
      newInputValue[index] = value;
      setRange(newInputValue);
    };

  const onBlurHandler = (index: number) => () => {
    setInputActing(false);
    if (index === 0) {
      onMinChange(range[0]);
    } else {
      onMaxChange(range[1]);
    }
  };

  const onSliderChange = useCallback(
    (values: number[]) => {
      if (inputActing) {
        return;
      }
      setRange([values[0].toString(), values[1].toString()]);
    },
    [inputActing, setRange]
  );

  useEffect(() => {
    setRange([minPriceValue.toString(), maxPriceValue.toString()]);
  }, [minPriceValue, maxPriceValue]);

  return {
    onSliderChange,
    onBlurHandler,
    onInputChange,
    initialMinPrice,
    initialMaxPrice,
    range,
  };
}
